import { Bug } from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import type { BugReportInput, BugSubmissionResult } from "@wmf/shared/bugReports";
import { ApiError, get, unwrap } from "../api.js";
import { useAuth } from "../auth.js";
import { Dialog } from "../ui/Dialog.js";
import { bugPageContext, captureBugPage, prepareBugImage } from "./capture.js";
import "./bugs.css";

type Draft = { context: ReturnType<typeof bugPageContext>; studentId: string | null; screenshot: File | null; captureMessage: string; screenshotsAvailable: boolean };
const BugReporterContext = createContext<{
  capturing: boolean; open: () => Promise<void>; button: RefObject<HTMLButtonElement>;
} | null>(null);

/** The form lives outside the responsive header, so moving its launcher
 * between desktop and mobile cannot discard an in-progress report. */
export function BugReporterProvider({ studentId = null, children }: { studentId?: string | null; children: ReactNode }) {
  const { me } = useAuth();
  const [capturing, setCapturing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [startError, setStartError] = useState("");
  const button = useRef<HTMLButtonElement>(null);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

  async function open() {
    if (capturing || draft) return;
    setCapturing(true); setStartError("");
    const context = bugPageContext(), address = window.location.href;
    let screenshot: File | null = null, captureMessage = "", screenshotsAvailable = false;
    try {
      let configTimer: ReturnType<typeof setTimeout> | undefined;
      const config = await Promise.race([
        get<{ screenshotsAvailable: boolean }>("/bugs/config").catch(() => ({ screenshotsAvailable: false })),
        new Promise<{ screenshotsAvailable: boolean }>(resolve => { configTimer = setTimeout(() => resolve({ screenshotsAvailable: false }), 3000); }),
      ]).finally(() => clearTimeout(configTimer));
      screenshotsAvailable = config.screenshotsAvailable;
      if (config.screenshotsAvailable) {
        try { screenshot = await captureBugPage(); }
        catch (error) {
          console.warn("Problem report screenshot capture failed", error);
          captureMessage = "Couldn't add a screenshot. You can still send your report.";
        }
      } else captureMessage = "Screenshots are unavailable at the moment. Your page link and report can still be sent.";
      if (window.location.href !== address) { screenshot = null; captureMessage = "The page changed while the screenshot was being captured. The report keeps the original page link."; }
      if (active.current) setDraft({ context, studentId, screenshot, captureMessage, screenshotsAvailable });
    } catch (error) { if (active.current) setStartError(error instanceof Error ? error.message : "The report form could not be opened. Try again."); }
    finally { if (active.current) setCapturing(false); }
  }
  if (!me) return <>{children}</>;
  return <BugReporterContext.Provider value={{ capturing, open, button }}>
    {children}
    {startError && <Dialog title="Report a problem" onClose={() => setStartError("")} restoreFocusRef={button}>
      <p role="alert">{startError}</p><button type="button" onClick={() => { setStartError(""); void open(); }}>Try again</button>
    </Dialog>}
    {draft && <BugReportForm key={me.user.id} draft={draft} onClose={() => setDraft(null)} restoreFocusRef={button} />}
  </BugReporterContext.Provider>;
}

export function BugReporter() {
  const reporter = useContext(BugReporterContext);
  if (!reporter) return null;
  return <button ref={reporter.button} type="button" className="quiet bug-report-launcher" data-bug-report-ignore
    aria-label={reporter.capturing ? "Preparing problem report" : "Report a problem"} title={reporter.capturing ? "Preparing problem report…" : "Report a problem"}
    disabled={reporter.capturing} onClick={() => void reporter.open()}><Bug size={20} aria-hidden="true" /></button>;
}

function BugReportForm({ draft, onClose, restoreFocusRef }: {
  draft: Draft; onClose: () => void; restoreFocusRef: RefObject<HTMLElement>;
}) {
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [screenshot, setScreenshot] = useState(draft.screenshot);
  const [captureMessage, setCaptureMessage] = useState(draft.captureMessage);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<BugSubmissionResult | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const attempt = useRef<{ input: BugReportInput; screenshot: File | null } | null>(null);
  const sending = useRef(false);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  async function retryCapture() {
    if (captureBusy) return;
    setCaptureBusy(true);
    try {
      setScreenshot(await captureBugPage());
      setCaptureMessage("");
    } catch (cause) {
      console.warn("Problem report screenshot capture failed", cause);
      setCaptureMessage("Couldn't add a screenshot. You can still send your report.");
    } finally {
      setCaptureBusy(false);
    }
  }
  useEffect(() => {
    if (!screenshot) { setPreview(""); return; }
    const url = URL.createObjectURL(screenshot); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (sending.current || imageBusy) return;
    sending.current = true; setBusy(true); setError("");
    if (!attempt.current) attempt.current = { input: { ...draft.context, submissionId: crypto.randomUUID(),
      studentId: draft.studentId, description: description.trim(), expected: expected.trim() }, screenshot };
    const current = attempt.current;
    const form = new FormData(); form.append("report", JSON.stringify(current.input));
    if (current.screenshot) form.append("screenshot", current.screenshot);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const saved = await unwrap<BugSubmissionResult>(await fetch("/api/bugs", { method: "POST", headers: { Accept: "application/json" }, body: form, signal: controller.signal }));
      setResult(saved); setUncertain(false);
    } catch (cause) {
      const definitive = cause instanceof ApiError && cause.status >= 400 && cause.status < 500;
      // Definite validation refusals are editable; a lost response reuses the
      // exact saved payload and key, including the original image.
      if (definitive || (cause instanceof ApiError && cause.status === 503)) { attempt.current = null; setUncertain(false); }
      else setUncertain(true);
      setError(cause instanceof Error ? cause.message : "Your report could not be sent. Try again.");
    } finally { clearTimeout(timer); sending.current = false; setBusy(false); }
  }

  return <Dialog title={result ? "Thank you for helping!" : "Report a problem"}
    description={result ? undefined : "Tell us what went wrong on this page."} size="medium"
    onClose={onClose} busy={busy || imageBusy} initialFocusRef={descriptionRef} restoreFocusRef={restoreFocusRef}
    className="bug-report-dialog" dismissOnBackdrop={false}>
    {result ? <div className="bug-thanks" role="status">
      <p>Your report has been sent to Wirral Music Factory.</p>
      <p>Your ticket number is <strong>{result.ticketNumber}</strong>.</p>
      <p>We’ll review your report to check whether the problem needs a fix.</p>
      <p>Thank you for helping us improve the Hub.</p>
      <button type="button" onClick={onClose}>Done</button>
    </div> : <form className="bug-report-form" onSubmit={event => void submit(event)}>
      <p className="small bug-page-link"><strong>Page:</strong> {draft.context.pagePath}</p>
      <fieldset disabled={busy || uncertain || imageBusy}>
        <label>What problem are you having?<textarea ref={descriptionRef} required minLength={10} maxLength={5000} rows={5}
          value={description} onChange={event => setDescription(event.target.value)} placeholder="Tell us what you were trying to do, what you pressed, and what happened." /></label>
        <label>What did you expect to happen? <span className="small muted">Optional</span><textarea maxLength={2000} rows={2}
          value={expected} onChange={event => setExpected(event.target.value)} /></label>
        <div className="bug-screenshot">
          <strong>Screenshot</strong>
          {preview ? <><img src={preview} alt="Screenshot attached to your problem report" /><button type="button" className="quiet" onClick={() => setScreenshot(null)}>Remove screenshot</button></>
            : <>
              <p className="small muted">{captureMessage || "No screenshot attached. You can send the report without one."}</p>
              {draft.screenshotsAvailable && <button type="button" className="quiet small" disabled={captureBusy || busy || imageBusy} onClick={() => void retryCapture()}>
                {captureBusy ? "Trying screenshot…" : "Try screenshot again"}
              </button>}
            </>}
          {draft.screenshotsAvailable && <label className="small">{preview ? "Replace screenshot" : "Add a screenshot"}<input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => {
            const file = event.target.files?.[0]; event.target.value = ""; if (!file) return;
            setImageBusy(true); setError("");
            void prepareBugImage(file).then(setScreenshot).catch(cause => setError(cause instanceof Error ? cause.message : "This image could not be opened.")).finally(() => setImageBusy(false));
          }} /></label>}
          <p className="small muted">Check the image before sending and remove it if it contains anything you do not want to share. Videos and embedded content may be missing.</p>
        </div>
      </fieldset>
      <p className="small muted">Your page link, account, browser and screen details are included to help us find the problem. Only the Hub owner can read reports.</p>
      {uncertain && <p role="status">We could not confirm the submission. Press “Retry submission” to check and safely send the same report.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="row"><button type="submit" disabled={busy || imageBusy || description.trim().length < 10}>{busy ? "Sending…" : uncertain ? "Retry submission" : "Send report"}</button>
        <button type="button" className="quiet" disabled={busy || imageBusy} onClick={onClose}>Cancel</button></div>
    </form>}
  </Dialog>;
}
